import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import authService, { LoginRequest, User } from '../services/authService';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../i18n/useLanguage';
import { useRedirect } from '../hooks/useRedirect';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserInfo: () => Promise<void>;
  getUserRoles: () => string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('auth_token');
  });
  const [user, setUser] = useState<User | null>(() => {
    // Lấy user từ localStorage khi khởi tạo
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  });
  const [isLoading, setIsLoading] = useState(false); // Không cần loading khi khởi tạo
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const { getRedirectAfterLogin } = useRedirect();

  // Chỉ fetch user data khi cần thiết (ví dụ: sau khi login)
  const { refetch: refetchUser } = useQuery({
    queryKey: ['user', token],
    queryFn: () => authService.getCurrentUserInfo(),
    enabled: false, // Không tự động fetch
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút
    retry: 1,
    retryDelay: 1000,
  });

  // Không cần useEffect để initialize auth nữa vì đã lấy từ localStorage
  // API client interceptor sẽ handle authentication expired

  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true);
      const response = await authService.login(credentials);
      const newToken = response.token;
      
      // Lưu token
      localStorage.setItem('auth_token', newToken);
      setToken(newToken);
      
      // Fetch user info mới sau khi login
      const userInfo = await authService.getCurrentUserInfo();
      setUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));
      
      // Thông báo thành công
      toast.success(t('auth.login.success'));
      
      // Chuyển hướng
      const redirectUrl = getRedirectAfterLogin();
      navigate(redirectUrl);
    } catch (error: any) {
      toast.error(error.message || t('auth.login.failure'));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear local state
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      
      // Clear queries
      queryClient.clear();
      
      // Thông báo
      toast.success(t('auth.login.logoutSuccess'));
      
      // Chuyển về trang đăng nhập
      navigate('/auth/signin');
    }
  };

  const refreshUserInfo = async () => {
    try {
      setIsLoading(true);
      const userInfo = await authService.getCurrentUserInfo();
      setUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));
    } catch (error) {
      console.error('Failed to refresh user info:', error);
      // Nếu không thể refresh, logout
      await logout();
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!(token && user),
    isLoading,
    login,
    logout,
    refreshUserInfo,
    getUserRoles: () => authService.getUserRoles(user),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
